import {createReducer} from "redux-act";
import {
	IFetchTeamsResponse,
	IPlayerActionsPayload,
	IReducerAction,
	IShareImageResponse,
	ITeamReducer,
	Lineup,
} from "modules/types";
import {
	addPlayerToTeam,
	fetchSharingImageClear,
	fetchSharingImageSuccess,
	fetchUserTeamsFailed,
	fetchUserTeams,
	fetchUserTeamsSuccess,
	removePlayerFromTeam,
	saveUserTeamClear,
	saveUserTeamRequest,
	saveUserTeamSuccess,
	setTeamChanged,
	sharingImageLoaded,
	setLineup,
} from "modules/actions";
import {PlayerPositionEnum, RequestStateType} from "modules/enums";

const emptyLineup = {
	[PlayerPositionEnum.QB]: null,
	[PlayerPositionEnum.WR]: null,
	[PlayerPositionEnum.RB]: null,
	[PlayerPositionEnum.OL]: null,
	[PlayerPositionEnum.DL]: null,
	[PlayerPositionEnum.LB]: null,
	[PlayerPositionEnum.DB]: null,
	[PlayerPositionEnum.K]: null,
	[PlayerPositionEnum.P]: null,
	[PlayerPositionEnum.ST]: null,
};

const initialState: ITeamReducer = {
	team: emptyLineup,
	history: [],
	isSaved: false,
	isTeamChanged: false,
	fetchRequestState: RequestStateType.Idle,
	saveRequestState: RequestStateType.Idle,
	shareImage: null,
	shareImageLoaded: false,
};

type ITeamReducerPayload<TP = null> = IReducerAction<ITeamReducer, TP>;

const onAddPlayerToTeam: ITeamReducerPayload<IPlayerActionsPayload> = (state, payload) => ({
	...state,
	team: {
		...state.team,
		[payload.position]: payload.playerId,
	},
});

const onRemovePlayerFromTeam: ITeamReducerPayload<PlayerPositionEnum> = (state, payload) => ({
	...state,
	team: {
		...state.team,
		[payload]: null,
	},
	isTeamChanged: true,
});

const onFetchUserTeams: ITeamReducerPayload<boolean> = (state) => ({
	...state,
	team: emptyLineup,
	fetchRequestState: RequestStateType.Loading,
});

const onFetchUserTeamsSuccess: ITeamReducerPayload<IFetchTeamsResponse> = (state, payload) => ({
	...state,
	fetchRequestState: RequestStateType.Success,
	history: payload.history,
});

const onFetchUserTeamsFailed: ITeamReducerPayload = (state) => ({
	...state,
	fetchRequestState: RequestStateType.Error,
});

const onSaveUserTeamRequest: ITeamReducerPayload = (state) => ({
	...state,
	saveRequestState: RequestStateType.Loading,
});

const onSaveUserTeamSuccess: ITeamReducerPayload<IFetchTeamsResponse> = (state, payload) => ({
	...state,
	saveRequestState: RequestStateType.Success,
	isSaved: true,
	isTeamChanged: false,
	history: payload.history,
	team: emptyLineup,
});

const onSaveUserTeamClear: ITeamReducerPayload = (state) => ({
	...state,
	saveRequestState: RequestStateType.Idle,
});

const onSetTeamChanged: ITeamReducerPayload = (state) => ({
	...state,
	isTeamChanged: true,
});

const onFetchSharingImageSuccess: ITeamReducerPayload<IShareImageResponse> = (state, payload) => ({
	...state,
	shareImage: payload.image || null,
	shareImageLoaded: true,
});

const onFetchSharingImageClear: ITeamReducerPayload = (state) => ({
	...state,
	shareImage: null,
});

const onSharingImageLoaded: ITeamReducerPayload<boolean> = (state, payload) => ({
	...state,
	shareImageLoaded: payload,
});

const onSetLineup: ITeamReducerPayload<Lineup> = (state, payload) => ({
	...state,
	team: payload,
});

export const teamReducer = createReducer({}, initialState)
	/*
	 * Actions with team
	 */
	.on(addPlayerToTeam, onAddPlayerToTeam)
	.on(removePlayerFromTeam, onRemovePlayerFromTeam)
	.on(setTeamChanged, onSetTeamChanged)
	/*
	 * Fetch team
	 */
	.on(fetchUserTeams, onFetchUserTeams)
	.on(fetchUserTeamsSuccess, onFetchUserTeamsSuccess)
	.on(fetchUserTeamsFailed, onFetchUserTeamsFailed)
	/*
	 * Save team
	 */
	.on(saveUserTeamRequest, onSaveUserTeamRequest)
	.on(saveUserTeamSuccess, onSaveUserTeamSuccess)
	.on(saveUserTeamClear, onSaveUserTeamClear)
	/*
	 * Sharing
	 */
	.on(fetchSharingImageSuccess, onFetchSharingImageSuccess)
	.on(fetchSharingImageClear, onFetchSharingImageClear)
	.on(sharingImageLoaded, onSharingImageLoaded)
	/*
	 * Actions with lineup
	 */
	.on(setLineup, onSetLineup);
